/**
 * useInvestorLanguage — investor-portal-scoped language state.
 *
 * Source of truth: `user.language` from GET /auth/me (persisted by backend).
 * Local cache: `localStorage.investor_language` for fast first paint, so the
 *   portal doesn't flash Spanish-then-English on reload for English users.
 *
 * Spanish remains the default and fallback. Missing translations resolve to
 * Spanish, then to the key itself — never crash or render `undefined`.
 *
 * Architecture: React Context, so every consumer in the investor subtree
 * shares the same state and a single `/auth/me` fetch on mount. Components
 * that need translation just call `useInvestorLanguage()`; the dashboard
 * wraps its subtree in `<InvestorLanguageProvider>`.
 *
 * Usage:
 *   <InvestorLanguageProvider>
 *     <InvestorDashboard />
 *   </InvestorLanguageProvider>
 *
 *   // inside any descendant
 *   const { t, lang, setLang } = useInvestorLanguage();
 *   t("tab.my_project")                        // → "Mi proyecto" / "My project"
 *   t("novedades.badge_many", { count: 3 })   // → "3 novedades"
 *
 * setLang(value) PATCHes /auth/me { language: value } and updates the cache.
 * It returns a promise and updates UI optimistically (cache + state set
 * before network completes).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import { api } from "@/lib/api";
import {
  translate, normalizeLanguage, DEFAULT_LANGUAGE,
} from "@/i18n/investorTranslations";

const STORAGE_KEY = "investor_language";

function readCachedLang() {
  try {
    return normalizeLanguage(localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_LANGUAGE;
  }
}
function writeCachedLang(v) {
  try { localStorage.setItem(STORAGE_KEY, normalizeLanguage(v)); } catch { /* noop */ }
}

// Fallback object used when a component renders outside the provider.
// Returning a working stub keeps the portal usable even if someone forgets
// to wrap (e.g. during isolated component tests).
const FALLBACK = {
  lang: DEFAULT_LANGUAGE,
  setLang: () => Promise.resolve(),
  t: (key, vars) => translate(DEFAULT_LANGUAGE, key, vars),
  busy: false,
  error: "",
};

const InvestorLanguageContext = createContext(FALLBACK);

export function InvestorLanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => readCachedLang());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Refresh from server once on mount — if it differs from cache, switch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        const serverLang = normalizeLanguage(data?.language);
        if (serverLang !== lang) {
          setLangState(serverLang);
          writeCachedLang(serverLang);
        }
      } catch {
        // Silent — fall back to cached / default.
      }
    })();
    return () => { cancelled = true; };
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback(async (next) => {
    const normalized = normalizeLanguage(next);
    setLangState(normalized);
    writeCachedLang(normalized);
    setBusy(true); setError("");
    try {
      await api.patch("/auth/me", { language: normalized });
    } catch (e) {
      setError(e?.message || "language_update_failed");
    } finally {
      setBusy(false);
    }
  }, []);

  const t = useCallback(
    (key, vars) => translate(lang, key, vars),
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t, busy, error }),
    [lang, setLang, t, busy, error]
  );

  return (
    <InvestorLanguageContext.Provider value={value}>
      {children}
    </InvestorLanguageContext.Provider>
  );
}

export default function useInvestorLanguage() {
  return useContext(InvestorLanguageContext);
}
